import React from 'react'
import { useSelector } from 'react-redux'
import { LanguageCode, State } from 'shared'

interface LogoInfoProps {
  lang: LanguageCode
}

const LogoInfo: React.FC<LogoInfoProps> = ({ lang }) => {
  const baseModelId = useSelector(
    (state: State) => state.bikeConfig.baseModelId
  )
  const isCargo = baseModelId.includes('cargo')
  return (
    <div className="velorbis-logo-info">
      {lang === 'da' && (
        <div>
          <p>
            <b>Krav til logofilen.</b>
            <br />
            Upload af logofil på cyklens reklameplader kræver filformatet .png
            for at jeres logo kan ses på cyklen i design toolet. Når I sidenhen
            har bestilt cyklen sendes en trykklar fil til os i en email og
            kravene til en trykklar fil er følgende:
          </p>
          <p>
            <b>Filformat</b>
            <br />
            Logoet laves i udskåret folie og I bedes sende en trykklar AI (.eps)
            fil - der er lag (layer) opdelt, vektoriseret og med outlinede
            tekster. Vi kan IKKE printe .jpg eller .png filer. Har I problemer
            med at fremskaffe denne fil bedes i kontakte jeres grafiker.
            Trykklar fil sendes til info@velorbis.com
          </p>
          {!isCargo && (
            <p>
              <b>Logo størrelser</b>
              <br />
              Maksimalt printarealet er 48 cm x 8 cm for centerplade på
              herrecykler og 30 cm x 10 cm på reklameplader på bagagebærere.
              Short John Unisex reklameplade maks mål er 50 cm x 15 cm. Jeres
              logo centreres på reklamepladerne. Hvis jeres logo skal være
              mindre en dette areal, skal i selv sætte den korrekte størrelse op
              i filen (trykklar). Vær opmærksom på at billeder i vores
              konfigurator kun er vejledende og ikke altid viser den fulde
              størrelse af logoet. Nogle logos kan ikke skaleres op til fuld
              printareal, hvis logo er meget langt eller meget højt.
            </p>
          )}
          {isCargo && (
            <p>
              <b>Logo størrelser</b>
              <br />
              De maksimale printareal på kassens sider er 30 cm (l) x 20 cm (h),
              og på kassens front er printarealet 40 cm (l) x 30 cm (h). Hvis
              dit logo skal være mindre end dette område, skal du selv angive
              den korrekte størrelse i trykfilerne (printklare filer). Bemærk,
              at billederne af ladcyklen i vores konfigurator kun er vejledende
              og ikke altid viser logoets fulde størrelse. Nogle logoer kan ikke
              tilpasses til det fulde trykområde, hvis logoet er meget langt
              eller højt.
            </p>
          )}
          <p>
            <b>Logo farver</b>
            <br />
            For at maksimere synligheden af jeres logo anbefaler vi altid at
            trykke det i enten sort eller hvid folie, afhængig af den valgte
            pulverlakerede farve på baggrundspladen. Ønsker i at beholde jeres
            specialfarver i logoet som ikke er 100% sort eller hvid, anbefaler
            vi at I vælger en sort eller hvid baggrundsplade, for at få logoet
            mere synligt. Er der andre farver end sort/hvid farver i jeres logo
            bedes i angive farvekode(r), når i fremsender den trykklare fil.
          </p>
          <p>
            <b>Logo folie vs. print</b>
            <br />
            Er jeres logo kompleks i form af at logoet har flere farvet og/eller
            indeholder mange detaljer, kan det ikke udskæres i folie og er ikke
            en del af vores standard pris. I bedes rette henvendelse til
            info@velorbis.com så kan vi give en pris på flerfarvet logo print.
          </p>
        </div>
      )}
      {lang === 'en' && (
        <div>
          <p>
            <b>Logo file requirements</b>
            <br />
            Uploading a logo file in the configurator and make it visible on the
            bike’s ad plates requires a .png file format. Once you have ordered
            the bike, you shall send the printable file to us in an email and
            the requirements for a printable file are as follows:
          </p>
          <p>
            <b>File format</b>
            <br />
            Please send a printable AI (.eps) file - layered, vectorized and
            with outlined text. We cannot print .jpg or .png files. If you have
            a problem obtaining this file please contact your graphic designer.
            The printable file can be sent to info@velorbis.com
          </p>
          {!isCargo && (
            <p>
              <b>Logo sizes</b>
              <br />
              The maximum print area is 48 cm x 8 cm for center plates on our
              Gents bikes and 30 cm x 10 cm on ad plates on our rear carriers.
              The Short John Unisex ad plate maximum dimensions are 50 cm x 15
              cm. Your logo is centered on the ad plates. If your logo should be
              smaller than this area, you must set the correct size in the file
              yourself (printable). Please note that bike images in our
              configurator are for guidance only and do not always show the full
              size of the logo. Some logos cannot be scaled up to full print
              area if the logo is very long or very high.
            </p>
          )}
          {isCargo && (
            <p>
              <b>Logo sizes</b>
              <br />
              The maximum print areas on box sides are 30 cm (l) x 20 cm (h) for
              box front print area is 40 cm (l) x 30 cm (h). If your logo should
              be smaller than this area, you must set the correct size in the
              print file yourself (printable). Please note that trike images in
              our configurator are for guidance only and do not always show the
              full size of the logo. Some logos cannot be scaled up to full
              print area if the logo is very long or very high.
            </p>
          )}
          <p>
            <b>Logo colours</b>
            <br />
            To maximize the visibility of your logo, we always recommend
            printing it in either black or white foil, depending on the selected
            powder-coated color on the background ad plate. If you want to keep
            your special colors in the logo which is not 100% black or white, we
            recommend that you choose a black or white background ad plate, to
            make the logo more visible. If there are colours other than black
            and white in your logo, please enter the color code(s) when
            submitting the printable file.
          </p>
          <p>
            <b>Logo print vs. foil</b>
            <br />
            If your logo is complex because it has multiple colours and/or
            contains many details, it can not be cut out in foil and is not part
            of our standard offer. Please contact info@velorbis.com so we can
            give a quote on multicolour logo print files.
          </p>
        </div>
      )}
    </div>
  )
}

export default LogoInfo
